import type { Geotab } from "@fairfleet/geotab";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { debounce, isEqual } from "lodash";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { pickStored } from "./pickStored";
import { userStateQueryKey, useUserStateRows } from "./useUserStateRows";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { AddInDataTooLargeError } from "@/utilities/addInData/upsertAddInData";
import { UserStateRow, writeUserState } from "@/utilities/addInData/userStateRecords";

/** Dragging a range or ticking through options is a burst of changes; only the settled one is written. */
const WRITE_DELAY_MS = 300;

type RowsData = { rows: UserStateRow[] };
/** Changes not yet confirmed by the store, per state key. They win over the stored rows. */
type PendingData = Record<string, unknown>;

const NO_PENDING: PendingData = {};

const hasKey = (record: PendingData, key: string) =>
  Object.prototype.hasOwnProperty.call(record, key);

export const userStatePendingKey = (activeSessionId: string | null, userId: string | undefined) =>
  [...userStateQueryKey(activeSessionId, userId), "pending"] as const;

interface PendingWrite {
  geotabClient: Geotab;
  activeSessionId: string | null;
  userId: string;
  key: string;
  payload: unknown;
}

export interface UserState<T> {
  value: T;
  setValue: (next: T | ((current: T) => T)) => void;
  /**
   * Stored value known. `false` while the current user is still being resolved; `true` with
   * defaults (writes disabled) once it is known there is no user.
   */
  isReady: boolean;
}

/**
 * One named piece of per-user state, stored in the `userState` AddInData bucket.
 *
 * Everything lives in the React Query cache: the fetched rows, and beside them the user's changes
 * that are not yet stored ("pending"). `value` is derived from both, pending first. `setValue`
 * never touches the fetched rows, so `isReady` keeps meaning "the stored rows are known"; a write
 * that lands moves its value into the rows and drops the pending entry unless a newer one replaced it.
 *
 * @param key - Names the state, e.g. `dateRange:faultMonitor`. Must be stable.
 * @param defaults - Shape and fallback values. Read once, on first render.
 */
export function useUserState<T extends Record<string, unknown>>(
  key: string,
  defaults: T,
): UserState<T> {
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { rows, isReady, userId } = useUserStateRows();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // The debounced writer is created once; it reads the latest notifier through this ref.
  const notifyFailure = useRef<(error: unknown) => void>(() => undefined);
  notifyFailure.current = error =>
    enqueueSnackbar(t(error instanceof AddInDataTooLargeError ? "userStateTooLarge" : "userStateSaveFailed"), {
      variant: "error",
      preventDuplicate: true,
    });
  const [initialDefaults] = useState(defaults);
  const inFlight = useRef<Promise<void>>(Promise.resolve());

  const { data: pending = NO_PENDING } = useQuery<PendingData>({
    queryKey: userStatePendingKey(activeSessionId, userId),
    queryFn: () => NO_PENDING,
    initialData: NO_PENDING,
    enabled: false,
    staleTime: Infinity,
  });

  const hasPending = hasKey(pending, key);
  const stored = hasPending ? pending[key] : rows.find(row => row.key === key)?.payload;
  const { value } = useMemo(
    () => pickStored(initialDefaults, stored),
    [initialDefaults, stored],
  );

  // Each pending write carries its own client, session and userId, captured when scheduled, so a
  // flush after a user or session switch still lands under the user who made the change. Writes are
  // chained so one never overlaps the previous: overlapping upserts would both miss the row and
  // Add twice, or land their Sets out of order.
  const write = useMemo(
    () =>
      debounce(
        ({
          geotabClient: client,
          activeSessionId: sid,
          userId: uid,
          key: k,
          payload,
        }: PendingWrite) => {
          inFlight.current = inFlight.current
            .then(() => writeUserState(client, uid, k, payload))
            .then(
              geotabId => {
                settleWrite(queryClient, sid, uid, {
                  geotabId,
                  key: k,
                  updatedAt: Date.now(),
                  payload,
                });
              },
              (error: unknown) => {
                // The pending entry stays, so the chosen value remains visible this session.
                console.error(`Could not store user state "${k}"`, error);
                notifyFailure.current(error);
              },
            );
        },
        WRITE_DELAY_MS,
        { leading: false, trailing: true },
      ),
    [queryClient],
  );

  // Flush when the page goes away, when the user/session/key changes, and on unmount.
  useEffect(() => {
    const flush = () => write.flush();
    const flushWhenHidden = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", flushWhenHidden);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", flushWhenHidden);
      flush();
    };
  }, [write, geotabClient, activeSessionId, userId, key]);

  const setValue = useCallback<UserState<T>["setValue"]>(
    next => {
      const pendingKey = userStatePendingKey(activeSessionId, userId);
      // Read the cache rather than the render's `value` so back-to-back updates compose.
      const currentPending = queryClient.getQueryData<PendingData>(pendingKey) ?? NO_PENDING;
      const currentStored = hasKey(currentPending, key)
        ? currentPending[key]
        : queryClient
            .getQueryData<RowsData>(userStateQueryKey(activeSessionId, userId))
            ?.rows.find(row => row.key === key)?.payload;
      const currentValue = pickStored(initialDefaults, currentStored).value;
      const nextValue = typeof next === "function" ? next(currentValue) : next;
      // An updater that keeps the value (e.g. a guard that only stores under a condition) must not
      // cost a write.
      if (isEqual(nextValue, currentValue)) return;

      queryClient.setQueryData<PendingData>(pendingKey, old => ({ ...old, [key]: nextValue }));

      // Without a user the pending entry is a session-only override; nothing is stored.
      if (userId) write({ geotabClient, activeSessionId, userId, key, payload: nextValue });
    },
    [activeSessionId, geotabClient, initialDefaults, key, queryClient, userId, write],
  );

  return { value, setValue, isReady };
}

/**
 * A write landed: record it in the fetched rows and drop the pending entry, unless the rows are not
 * loaded yet (the pending entry keeps covering them) or a newer change has replaced the entry.
 */
function settleWrite(
  queryClient: ReturnType<typeof useQueryClient>,
  activeSessionId: string | null,
  userId: string,
  written: UserStateRow,
) {
  const rowsKey = userStateQueryKey(activeSessionId, userId);
  if (!queryClient.getQueryData<RowsData>(rowsKey)) return;
  queryClient.setQueryData<RowsData>(rowsKey, old => {
    if (!old) return old;
    const others = old.rows.filter(row => row.key !== written.key);
    return { ...old, rows: [...others, written] };
  });
  queryClient.setQueryData<PendingData>(userStatePendingKey(activeSessionId, userId), old => {
    if (!old || !hasKey(old, written.key) || old[written.key] !== written.payload) return old;
    const rest = { ...old };
    delete rest[written.key];
    return rest;
  });
}
